import * as zod from 'zod';

import type { DeviceInfo } from '@sb/messenger/auto_generated/messenger/skills_bot_service';

/**
 * Base configuration for completion detection methods.
 * - For learned completion/failure: threshold is the model prediction confidence (0-1)
 * - For movement completion: threshold is the position delta in mm (e.g., 5 = 5mm)
 */
const CompletionConfig = zod.object({
  enabled: zod.boolean().default(false),
  threshold: zod.number(),
  consecutiveCount: zod.number().int(),
});

/**
 * Movement completion configuration.
 * threshold: position delta in mm. rotationThreshold: max rotation magnitude in degrees; if exceeded, movement completion fails.
 * gripperThreshold: if gripper delta across horizon exceeds this, movement completion fails. Default 0.1.
 */
const MovementCompletionConfig = CompletionConfig.extend({
  rotationThreshold: zod.number().default(5), // degrees
  gripperThreshold: zod.number().default(0.1),
});

/**
 * Consolidated completion prediction configuration for start_inference command
 */
export const CompletionPredictionConfig = zod.object({
  learnedCompletion: CompletionConfig.default({
    enabled: false,
    threshold: 0.75,
    consecutiveCount: 2,
  }),
  learnedFailure: CompletionConfig.default({
    enabled: false,
    threshold: 0.75,
    consecutiveCount: 2,
  }),
  movementCompletion: MovementCompletionConfig.default({
    enabled: false,
    threshold: 5, // mm default
    consecutiveCount: 3,
    rotationThreshold: 5, // degrees default
    gripperThreshold: 0.1,
  }),
});

export type CompletionPredictionConfig = zod.infer<
  typeof CompletionPredictionConfig
>;

// Skill subcommand types
export const GPUTaskRunnerSkillSubCommand = zod.union([
  zod.object({
    command: zod.literal('start_inference'),
    timeout: zod.number(),
    modelName: zod.string(),
    completionPrediction: CompletionPredictionConfig.optional(),
    caption: zod.string().nullable().optional(),
    frequency: zod.number().optional(),
    stepsPerInference: zod.number().optional(),
    maxLinearSpeed: zod.number().nullable().optional(),
    actionExecutionMode: zod.string().optional(),
    actionInterpolationMethod: zod.string().optional(),
    outputSmoothingAlpha: zod.number().optional(),
    enablePredictionLogging: zod.boolean().optional(),
    lockedAxesPerRobot: zod
      .record(zod.string(), zod.array(zod.number().int().min(0).max(6)))
      .optional(),
    axisLockingFramePerRobot: zod
      .record(zod.string(), zod.enum(['world', 'wrist']))
      .optional(),
  }),
  zod.object({
    command: zod.literal('stop_inference'),
  }),
  zod.object({
    command: zod.literal('configure_inference'),
    modelName: zod.string(),
    robotIdsMap: zod.record(zod.string(), zod.string()), // {[robot key]: [robot ID]}, e.g. {'robot_0': 'bot_0sapi_rvVAMObHwVGCkTkpVSY6'}
    equipmentKeyToDeviceInfo: zod.record(
      zod.string(),
      zod.custom<DeviceInfo>(),
    ),
    useGripperMask: zod.boolean().optional(),
    robotConfig: zod.string(),
    obsHorizon: zod.number().optional(),
    obsRes: zod.array(zod.number()).optional(), // [width, height]
    robotControllerFrequency: zod.number().optional(),
  }),
  zod.object({
    command: zod.literal('teardown_inference'),
  }),
  zod.object({
    command: zod.literal('delete_model'),
    modelName: zod.string(),
  }),
]);

// Vision subcommand types
const GPUTaskRunnerRunInferenceSubCommand = zod.object({
  command: zod.literal('run_inference'),
  image: zod.string(), // base64 encoded image
  model_path: zod.string(),
  model_type: zod.string(), // Valid values: 'YoloV11', 'YoloV8-OBB', 'TextPrompt'
  thresholding_params: zod.record(zod.string(), zod.string()).optional(),
  include_masks: zod.boolean().optional(), // request SAM2 masks in Blob2D response
});

const GPUTaskRunnerPreloadRoboflowModelSubCommand = zod.object({
  command: zod.literal('preload_roboflow_model'),
  // `RoboflowModel.url`, which IS the inference model_id — "<project>/<version>"
  // for versioned models, "<workspace>/<model>" for NAS. Passed to vision-bot
  // unchanged (stripping a segment yields a bare version and 404s).
  modelUrl: zod.string(),
});

// Roboflow *workflows* are descoped from the skill UI — the picker offers models
// only. This subcommand and the vision-bot RPC behind it are kept deliberately so
// the transport exists when workflows come back; nothing in the app sends it yet.
const GPUTaskRunnerPreloadRoboflowWorkflowSubCommand = zod.object({
  command: zod.literal('preload_roboflow_workflow'),
  workspaceName: zod.string(),
  workflowId: zod.string(),
});

const GPUTaskRunnerRunRoboflowDetectionSubCommand = zod.object({
  command: zod.literal('run_roboflow_detection'),
  image: zod.string(), // base64 encoded image
  // Full Roboflow inference id "<workspace>/<project>/<version>" (RoboflowModel.url).
  modelUrl: zod.string(),
  // Confidence / nms_threshold / max_detections — vision-bot applies these after
  // a raw inference (the model's own thresholds are bypassed).
  thresholding_params: zod.record(zod.string(), zod.string()).optional(),
});

export const GPUTaskRunnerVisionSubCommand = zod.discriminatedUnion('command', [
  GPUTaskRunnerRunInferenceSubCommand,
  GPUTaskRunnerPreloadRoboflowModelSubCommand,
  GPUTaskRunnerPreloadRoboflowWorkflowSubCommand,
  GPUTaskRunnerRunRoboflowDetectionSubCommand,
]);

/** Result of a Roboflow preload vision subcommand (returned to the browser). */
export type GPUTaskRunnerRoboflowPreloadResult =
  | { ok: true; loadTimeMs?: number }
  | { ok: false; error: string };

export const GPUTaskRunnerCommand = zod.object({
  kind: zod.literal('GPUTaskRunnerCommand'),
  equipmentId: zod.optional(zod.string()),
  commandType: zod.enum(['skill', 'vision']),
  subCommand: zod.union([
    GPUTaskRunnerSkillSubCommand,
    GPUTaskRunnerVisionSubCommand,
  ]),
});

export type GPUTaskRunnerCommand = zod.infer<typeof GPUTaskRunnerCommand>;
export type GPUTaskRunnerSkillSubCommand = zod.infer<
  typeof GPUTaskRunnerSkillSubCommand
>;
export type GPUTaskRunnerVisionSubCommand = zod.infer<
  typeof GPUTaskRunnerVisionSubCommand
>;
